// localStorage helpers shared by every mock endpoint. Keys were bumped to v9
// when Invoice was realigned to backend cents-based shape (totalCents /
// paidAmountCents / period object / invoiceNumber). v8 invoice seed was
// dollar-mixed and is incompatible; legacy keys are removed on hydrate.
// Mock is demo-only so no migration is performed.

export const STORAGE_KEYS = {
  deals: 'deal_mgmt_v9_deals',
  state: 'deal_mgmt_v9_state',
  dealInvoices: 'deal_mgmt_v9_deal_invoices',
  dealShipments: 'deal_mgmt_v9_deal_shipments',
  idempotency: 'deal_mgmt_v9_idem',
} as const

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]

// Legacy v7 + v8 keys — removed on hydrate. Demo state, not durable; we wipe
// instead of migrating so a backend-shape-aligned seed lands cleanly.
const LEGACY_KEYS = [
  // v7 (pre Deal entity realignment)
  'deal_mgmt_v7_deals_v2',
  'deal_mgmt_v7_state',
  'deal_mgmt_v7_invoices_v2',
  'deal_mgmt_v7_shipments_v2',
  'deal_mgmt_v7_idem_v2',
  // v8 (pre Invoice cents alignment)
  'deal_mgmt_v8_deals',
  'deal_mgmt_v8_state',
  'deal_mgmt_v8_deal_invoices',
  'deal_mgmt_v8_deal_shipments',
  'deal_mgmt_v8_idem',
]

export function pruneLegacyKeys(): void {
  for (const key of LEGACY_KEYS) {
    if (window.localStorage.getItem(key) !== null) {
      window.localStorage.removeItem(key)
    }
  }
}

export function readJson<T>(key: StorageKey, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function writeJson(key: StorageKey, value: unknown): void {
  window.localStorage.setItem(key, JSON.stringify(value))
}

export function hasKey(key: StorageKey): boolean {
  return window.localStorage.getItem(key) !== null
}

const MIN_DELAY_MS = 50
const MAX_DELAY_MS = 250

export function delay<T>(value: T): Promise<T> {
  const ms = MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS)
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), ms)
  })
}
